// PrintInvoicePage.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { getSaleById } from "../../actions/saleAction";
import { Link } from "react-router-dom";
import {
  getStoreInvoiceFooter,
  getStoreLogo,
  getStoreQris,
  getStoreReceiptName,
  normalizeStoreSetting,
} from "../../utils/helpers/storeSetting";
import PATH_URL from "../../routers/path";

const PrintInvoicePage = ({ sale, isLoading, getSaleById, storeSetting }) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();

  // Ambil data dari URL state atau fetch berdasarkan ID
  const saleData = location.state?.saleData;
  const baseInvoice = saleData || sale;
  const requestedBillId = new URLSearchParams(location.search).get("bill_id");
  const invoice = useMemo(() => {
    const selectedBill = requestedBillId
      ? (baseInvoice?.bills || []).find((bill) => String(bill?.id) === String(requestedBillId))
      : null;
    if (!selectedBill) return baseInvoice;

    return {
      ...baseInvoice,
      invoice_no: `${baseInvoice?.invoice_no || `INV-${baseInvoice?.id}`} · ${selectedBill.bill_no}`,
      customer_name: selectedBill.customer_names?.join(", ") || baseInvoice?.customer_name,
      details: (selectedBill.items || []).map((item) => ({ ...item, sub_total: item.subtotal })),
      subtotal: selectedBill.subtotal,
      discount: selectedBill.discount,
      tax: selectedBill.tax,
      total: selectedBill.total,
      pay: selectedBill.pay,
      due: selectedBill.due,
      change: selectedBill.change,
      payment_method: selectedBill.payment_method,
      payment_status: selectedBill.payment_status === "partial" ? "unpaid" : selectedBill.payment_status,
      created_at: selectedBill.created_at || baseInvoice?.created_at,
    };
  }, [baseInvoice, requestedBillId]);
  const invoiceTimestamp = invoice?.created_at || invoice?.sale_date || "";

  useEffect(() => {
    // Jika tidak ada data di state, fetch berdasarkan ID
    if (!location.state?.saleData && id) {
      getSaleById(id);
    }
  }, [id, saleData, getSaleById, location.state?.saleData]);

  useEffect(() => {
    const handleNativePrint = () => {
      if (!invoice) {
        window.alert("Data invoice belum siap. Silakan coba kembali.");
        return;
      }

      if (!window.AkasPOS?.printReceipt) {
        console.info("AkasPOS bridge tidak tersedia. Halaman sedang dibuka di browser biasa.");
        return;
      }

      try {
        const companyData = normalizeStoreSetting(storeSetting);
        const companyName = getStoreReceiptName(companyData);
        const outlet = companyData.outlet || {};
        const details = Array.isArray(invoice.details) ? invoice.details : [];
        const subtotal = details.reduce((total, item) => total + Number(item?.sub_total || 0), 0);
        const invoiceNumber =
          invoice?.invoice_no || invoice?.invoice_number || `INV-${String(invoice?.id || id).padStart(6, "0")}`;

        const receipt = {
          storeName: companyName,
          address: outlet.address || companyData.address || "",
          email: outlet.email || companyData.email || "",
          phone: outlet.phone || companyData.phone || "",
          logo: getStoreLogo(companyData, ""),
          qris: getStoreQris(companyData, ""),
          invoiceId: String(invoice?.id || id || ""),
          invoiceNumber,
          date: invoiceTimestamp,
          customer: invoice.customer_name || "Umum",
          cashier: invoice.cashier_name || "Kasir",
          items: details.map((item) => ({
            name: item?.product_name || "Produk",
            qty: Number(item?.qty || 0),
            price: Number(item?.unit_price || 0),
            subtotal: Number(item?.sub_total || 0),
            note: item?.note || "",
          })),
          subtotal,
          discount: Number(invoice.discount || 0),
          tax: Number(invoice.tax || 0),
          total: Number(invoice.total || 0),
          paymentMethod: invoice.payment_method || "",
          paid: Number(invoice.pay || 0),
          change: Number(invoice.change || 0),
          footer: getStoreInvoiceFooter(companyData),
        };

        window.AkasPOS.printReceipt(JSON.stringify(receipt));
      } catch (error) {
        console.error("Gagal menyiapkan struk Android:", error);
        window.alert(error?.message || "Gagal menyiapkan data struk.");
      }
    };

    window.addEventListener("akas:native-print", handleNativePrint);
    return () => window.removeEventListener("akas:native-print", handleNativePrint);
  }, [id, invoice, invoiceTimestamp, storeSetting]);

  const formatRupiah = (angka) => {
    return `Rp ${(angka || 0).toLocaleString("id-ID")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const normalizedDate = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(dateString)
      ? dateString.replace(" ", "T")
      : dateString;
    const date = new Date(normalizedDate);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Jika loading
  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <div>Memuat data invoice...</div>
      </div>
    );
  }

  // Jika tidak ada data
  if (!invoice) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <div>Data invoice tidak ditemukan</div>
        <Link to={PATH_URL.SALE} className="mt-4">
          Tutup
        </Link>
      </div>
    );
  }

  const globalCompany = normalizeStoreSetting(storeSetting);
  const activeOutlet = globalCompany.outlet || {};
  const company = {
    ...globalCompany,
    address: activeOutlet.address || globalCompany.address,
    email: activeOutlet.email || globalCompany.email,
    phone: activeOutlet.phone || globalCompany.phone,
  };
  const companyName = getStoreReceiptName(company);
  const invoiceFooter = getStoreInvoiceFooter(company);
  const logoImage = getStoreLogo(company, "");
  const paymentStatus = String(invoice?.payment_status || "").toLowerCase();
  const isPaid = paymentStatus === "paid";
  const isCancelled = paymentStatus === "cancelled";
  const paymentStatusLabel = isPaid ? "✓ LUNAS" : isCancelled ? "DIBATALKAN" : "BELUM LUNAS";
  const invoiceNumber = invoice?.invoice_no || invoice?.invoice_number || `INV-${String(invoice.id).padStart(6, "0")}`;

  return (
    <div ref={printRef} className="receipt-print-page" style={{ padding: 20, fontFamily: "Courier New, monospace" }}>
      <style>
        {`
          .receipt-print-page {
            min-height: 100vh;
            background: #f8fafc !important;
            color: #111827 !important;
          }

          .invoice-back-action {
            position: fixed;
            top: 18px;
            left: 18px;
            z-index: 10;
            display: inline-flex;
            width: 44px;
            height: 44px;
            align-items: center;
            justify-content: center;
            color: #15373c;
            border: 1px solid #d8e2de;
            border-radius: 14px;
            background: rgba(255, 255, 255, .94);
            box-shadow: 0 12px 30px rgba(15, 45, 49, .12);
            cursor: pointer;
            backdrop-filter: blur(12px);
            transition: transform .18s ease, box-shadow .18s ease, background .18s ease;
          }

          .invoice-back-action:hover {
            color: #0b2d32;
            background: #fff;
            box-shadow: 0 16px 36px rgba(15, 45, 49, .17);
            transform: translateX(-2px);
          }

          .invoice-back-action svg {
            width: 22px;
            height: 22px;
          }

          [data-bs-theme="dark"] .receipt-print-page {
            background: #f8fafc !important;
            color: #111827 !important;
          }

          @media print {
            body {
              margin: 0;
              padding: 0;
              background: white !important;
              color: #111827 !important;
            }
            .no-print {
              display: none;
            }
            .invoice {
              margin: 0 auto;
              box-shadow: none;
              color: #111827 !important;
            }
            @page {
              size: auto;
              margin: 0mm;
            }
          }
          
          .invoice {
            width: 300px;
            margin: 0 auto;
            background: white;
            color: #111827 !important;
            box-shadow: 0 16px 50px rgba(15, 23, 42, 0.12);
            padding: 10px 12px;
          }

          .invoice *,
          [data-bs-theme="dark"] .invoice * {
            color: #111827;
          }
          
          .text-center {
            text-align: center;
          }
          
          .border-top {
            border-top: 1px dashed #000;
            margin: 8px 0;
          }
          
          .border-bottom {
            border-bottom: 1px dashed #000;
            margin: 8px 0;
          }
          
          .flex {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }
          
          .bold {
            font-weight: bold;
          }
          
          .item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
            font-size: 11px;
          }
          
          .item-name {
            flex: 2;
          }

          .item-note {
            margin: -1px 0 5px;
            padding-left: 2px;
            color: #4b5563 !important;
            font-size: 9px;
            font-style: italic;
          }
          
          .item-qty {
            flex: 0.5;
            text-align: center;
          }
          
          .item-unit {
            flex: 0.8;
            text-align: center;
          }
          
          .item-price {
            flex: 1;
            text-align: right;
          }
          
          .item-total {
            flex: 1;
            text-align: right;
          }
          
          .total {
            margin-top: 10px;
            padding-top: 5px;
            border-top: 1px dashed #000;
            font-weight: bold;
            font-size: 14px;
          }
          
          .footer {
            text-align: center;
            margin-top: 15px;
            font-size: 10px;
          }
          
          .thankyou {
            font-size: 12px;
            font-weight: bold;
            margin: 10px 0;
          }
          
          .payment-status {
            display: inline-block;
            padding: 2px 6px;
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.2px;
          }
          
          .status-paid {
            color: green !important;
          }
          
          .status-unpaid,
          .status-draft,
          .status-pending {
            color: orange !important;
          }

          .status-cancelled {
            color: #d63939 !important;
          }
          
          .invoice-action {
            margin-bottom: 20px;
            padding: 10px 20px;
            cursor: pointer;
            background: #059669;
            color: white;
            border: none;
            border-radius: 5px;
            font-size: 14px;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          
          .invoice-action:hover {
            background: #047857;
            color: white;
          }
        `}
      </style>

      <button
        type="button"
        className="invoice-back-action no-print"
        aria-label="Kembali ke kasir"
        title="Kembali ke kasir"
        onClick={() => navigate(PATH_URL.POS)}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true">
          <path d="M15 18l-6-6l6-6" />
        </svg>
      </button>

      <div className="invoice">
        {/* Header */}
        <div className="text-center">
          {logoImage ? (
            <img src={logoImage} alt={companyName} style={{ width: 135, marginBottom: 8, marginTop: 10 }} />
          ) : null}
          <h3 style={{ margin: "6px 0 5px" }}>{companyName}</h3>
          {company.address ? <div style={{ fontSize: 10 }}>{company.address}</div> : null}
          {company.email ? <div style={{ fontSize: 9 }}>{company.email}</div> : null}
          {company.phone ? <div style={{ fontSize: 10 }}>{company.phone}</div> : null}
        </div>

        <div className="border-top"></div>

        {/* Invoice Info */}
        <div className="flex">
          <span>No. Invoice</span>
          <span className="bold">{invoiceNumber}</span>
        </div>
        <div className="flex">
          <span>Tanggal</span>
          <span className="fs-5">{formatDate(invoiceTimestamp)}</span>
        </div>
        <div className="flex">
          <span>Customer</span>
          <span>{invoice.customer_name || "Umum"}</span>
        </div>
        <div className="flex">
          <span>Kasir</span>
          <span>{invoice.cashier_name || "Kasir"}</span>
        </div>

        <div className="border-top"></div>

        {/* Payment Method */}
        <div className="flex">
          <span>Metode Bayar</span>
          <span className="bold">
            {invoice.payment_method === "cash" && "Tunai"}
            {invoice.payment_method === "qris" && "QRIS"}
            {invoice.payment_method === "card" && "Kartu"}
            {invoice.payment_method === "transfer" && "Transfer"}
          </span>
        </div>
        <div className="flex">
          <span>Status</span>
          <span className={`payment-status status-${paymentStatus || "unpaid"} ${!isPaid ? "bold" : ""}`}>
            {paymentStatusLabel}
          </span>
        </div>

        <div className="border-top"></div>

        {/* Items Header */}
        <div className="item bold">
          <span className="item-name">Item</span>
          <span className="item-qty">Qty</span>
          <span className="item-price">Harga</span>
          <span className="item-total">Total</span>
        </div>

        {/* Items List */}
        {invoice.details &&
          invoice.details.map((item, i) => (
            <React.Fragment key={i}>
              <div className="item">
                <span className="item-name">{item.product_name}</span>
                <span className="item-qty">{item.qty}</span>
                <span className="item-price">{item.unit_price}</span>
                <span className="item-total">{formatRupiah(item.sub_total)}</span>
              </div>
              {item?.note ? <div className="item-note">Catatan: {item.note}</div> : null}
            </React.Fragment>
          ))}

        <div className="border-top"></div>

        {/* Summary */}
        <div className="flex">
          <span>Subtotal</span>
          <span>{formatRupiah(invoice?.details?.reduce((acc, item) => acc + item.sub_total, 0))}</span>
        </div>

        {invoice.discount > 0 && (
          <div className="flex">
            <span>Diskon</span>
            <span>- {formatRupiah(invoice.discount)}</span>
          </div>
        )}

        <div className="flex total">
          <span>TOTAL</span>
          <span>{formatRupiah(invoice.total)}</span>
        </div>

        <div className="flex">
          <span>Bayar</span>
          <span>{formatRupiah(invoice.pay)}</span>
        </div>
        <div className="flex">
          <span>Kembalian</span>
          <span className="bold">{formatRupiah(invoice.change)}</span>
        </div>
        <div className="flex justify-content-center mt-5">
          <span className="fw-bold fs-6">Terimakasih</span>
        </div>

        {/* Footer */}
        <div className="footer" style={{ textAlign: "center", marginBottom: "6em" }}>
          {invoiceFooter ? <div className="thankyou">{invoiceFooter}</div> : null}
          {company.website ? <div style={{ fontSize: 8, fontWeight: "bold" }}>{company.website}</div> : null}
        </div>
      </div>
      {/* <div className="no-print mt-5 d-flex w-100 justify-content-center mx-auto">
        <button type="button" className="invoice-action" onClick={() => window.print()}>
          🖨️ Cetak / Print
        </button>
      </div> */}
    </div>
  );
};

const mapStateToProps = (state) => ({
  sale: state?.sales?.selected || null,
  isLoading: state?.sales?.loading || false,
  storeSetting: state?.stores?.setting,
});

const mapDispatchToProps = {
  getSaleById,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrintInvoicePage);
